import {
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconFolder,
  IconFolderSymlink,
} from "@tabler/icons-react";
import React, { useState } from "react";
import { client } from "@/api-client";
import type { FileOutput, FileType, FolderOutput } from "@/api-types";
import { FancyInlineLoader } from "@/components/FancyLoader";
import { LibraryItem } from "@/components/LibraryItem";

// Types are now imported directly from @/api-types

const ROOT_FOLDER: FolderState = {
  folderId: null,
  folderName: "Your Library",
};

interface FolderState {
  folderId: string | null;
  folderName: string;
}

interface FolderNavigatorProps extends UseFolderNavigatorProps {
  children: React.ReactElement<{ onClick?: () => void }>;
}

interface UseFolderNavigatorProps {
  onFileSelect: (file: FileOutput) => void;
  onFolderSelect?: (folder: FolderOutput) => void;
  allowedFileTypes?: FileType[];
}

interface UseFolderNavigatorReturn {
  open: () => void;
  modal: React.ReactNode;
}

export const useFolderNavigator = ({
  onFileSelect,
  onFolderSelect,
  allowedFileTypes,
}: UseFolderNavigatorProps): UseFolderNavigatorReturn => {
  const [opened, { open, close }] = useDisclosure(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<FolderState>(ROOT_FOLDER);

  // Query files and folders
  const { data: filesData } = client.useQuery(
    "get",
    "/universal/files/",
    {},
    {
      staleTime: 0,
      gcTime: 0,
      enabled: opened && !hasLoadedData,
      onSuccess: () => {
        setHasLoadedData(true);
      },
    },
  );

  const handleFileSelect = (file: FileOutput) => {
    onFileSelect(file);
    close();
  };

  const handleFolderSelect = (folder: FolderOutput) => {
    if (onFolderSelect) {
      onFolderSelect(folder);
      close();
    }
  };

  // Go back to parent folder
  const navigateUp = () => {
    if (!currentFolder.folderId) {
      return;
    }

    if (!filesData) {
      return;
    }

    // Find the parent folder from the files data
    const currentFolderData = filesData.folders.find(
      (f) => f.id === currentFolder.folderId,
    );
    if (currentFolderData?.parentFolderId) {
      const parentFolder = filesData.folders.find(
        (f) => f.id === currentFolderData.parentFolderId,
      );
      if (parentFolder) {
        setCurrentFolder({
          folderId: parentFolder.id,
          folderName: parentFolder.name,
        });
      } else {
        // If no parent folder found, go to root
        setCurrentFolder(ROOT_FOLDER);
      }
    } else {
      // If no parent folder ID, go to root
      setCurrentFolder(ROOT_FOLDER);
    }
  };

  const renderModal = (
    data: { files: FileOutput[]; folders: FolderOutput[] } | undefined,
  ) => {
    if (!data) {
      return (
        <Modal
          opened={opened}
          onClose={close}
          title="Pick a File or Folder"
          size="lg"
        >
          <FancyInlineLoader />
        </Modal>
      );
    }

    // Filter files and folders based on current folder and allowed file types
    const currentFolders = data.folders.filter(
      (folder: FolderOutput) =>
        folder.parentFolderId === currentFolder.folderId,
    );
    const currentFiles = data.files.filter(
      (file: FileOutput) =>
        file.parentFolderId === currentFolder.folderId &&
        (!allowedFileTypes || allowedFileTypes.includes(file.type)),
    );

    const currentFolderId = currentFolder.folderId;
    const currentFolderObject =
      data.folders.find((f) => f.id === currentFolderId) ?? null;

    return (
      <Modal
        opened={opened}
        onClose={close}
        title="Pick a File or Folder"
        size="lg"
      >
        <Stack gap="xs">
          {/* Current folder name */}
          <Text size="md" fw={500}>
            {currentFolder.folderName}
          </Text>

          {/* Folder actions */}
          <Group>
            {currentFolderId && (
              <>
                <Button
                  leftSection={<IconArrowLeft size="1.2em" />}
                  variant="subtle"
                  onClick={navigateUp}
                >
                  Back
                </Button>
                {currentFolderObject && (
                  <Button
                    leftSection={<IconFolderSymlink size="1.2em" />}
                    variant="filled"
                    onClick={() => handleFolderSelect(currentFolderObject)}
                  >
                    Select Folder
                  </Button>
                )}
              </>
            )}
          </Group>

          {/* Display folders first */}
          {currentFolders.length > 0 && (
            <Box mb="md">
              <Title order={5} mb="xs">
                Folders
              </Title>
              <Stack gap="md">
                {currentFolders.map((folder: FolderOutput) => (
                  <LibraryItem
                    key={folder.id}
                    item={folder}
                    kind="folder"
                    readonly={true}
                    onFolderClick={() =>
                      setCurrentFolder({
                        folderId: folder.id,
                        folderName: folder.name,
                      })
                    }
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Display files */}
          {currentFiles.length > 0 && (
            <Box>
              <Title order={5} mb="xs">
                Files
              </Title>
              <Stack gap="md">
                {currentFiles.map((file: FileOutput) => (
                  <div key={file.id} onClick={() => handleFileSelect(file)}>
                    <LibraryItem
                      item={file}
                      kind="file"
                      readonly={true}
                      onFileClick={(_) => handleFileSelect(file)}
                    />
                  </div>
                ))}
              </Stack>
            </Box>
          )}

          {currentFolders.length === 0 && currentFiles.length === 0 && (
            <Paper withBorder>
              <Stack align="center" py="xl">
                <IconFolder size={48} opacity={0.5} />
                <Text size="lg" fw={500}>
                  {currentFolder.folderId
                    ? "This folder is empty"
                    : "Your library is empty, please upload some files first."}
                </Text>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Modal>
    );
  };

  return {
    open,
    modal: renderModal(filesData),
  };
};

export const FolderNavigator: React.FC<FolderNavigatorProps> = ({
  onFileSelect,
  onFolderSelect,
  allowedFileTypes,
  children,
}) => {
  const { open, modal } = useFolderNavigator({
    onFileSelect,
    onFolderSelect,
    allowedFileTypes,
  });

  return (
    <>
      {React.cloneElement(children, {
        onClick: open,
      })}
      {modal}
    </>
  );
};
